import {
  SUPPORTED_LANGUAGES,
  type CovalVoice,
} from "@/types/ModelConfigs/CovalVoiceConstants"
import { LANGUAGE_DISPLAY_MAP } from "@/types/ModelConfigs/LanguageDisplayNames"

import { voiceSupportsLanguageCode } from "../utils/personaModalUtils"

export type VoicePitch = NonNullable<CovalVoice["pitch"]>
export type VoiceFilterKey =
  | `presentation:${CovalVoice["voicePresentation"]}`
  | `pitch:${VoicePitch}`

export const VOICE_PRESENTATION_FILTER_OPTIONS: Array<{
  value: CovalVoice["voicePresentation"]
  label: string
}> = [
  { value: "Masculine", label: "Masculine" },
  { value: "Feminine", label: "Feminine" },
]

export const VOICE_PITCH_FILTER_OPTIONS: Array<{
  value: VoicePitch
  label: string
}> = [
  { value: "low", label: "Low pitch" },
  { value: "medium", label: "Medium pitch" },
  { value: "high", label: "High pitch" },
]

const PREFERRED_LANGUAGE_CODES: Partial<Record<string, string>> = {
  de: "de-DE",
  en: "en-US",
  es: "es-US",
  fr: "fr-FR",
  hi: "hi-IN",
  ko: "ko-KR",
  pt: "pt-BR",
  // Picking Chinese resolves to Mandarin. Stated here rather than left to fall
  // out of the order of SUPPORTED_LANGUAGES.
  zh: "zh-CN",
}

/**
 * Country a bare language family flies. A regional code names its own country,
 * but a family does not, so each one takes the country its speakers are most
 * associated with — Arabic flies Saudi Arabia, Portuguese flies Brazil.
 */
const LANGUAGE_FAMILY_FLAG_REGIONS: Record<string, string> = {
  ar: "SA",
  bg: "BG",
  bn: "BD",
  cs: "CZ",
  hr: "HR",
  hu: "HU",
  ro: "RO",
  sv: "SE",
  da: "DK",
  de: "DE",
  en: "US",
  es: "ES",
  fr: "FR",
  hi: "IN",
  ja: "JP",
  ko: "KR",
  lt: "LT",
  pl: "PL",
  pt: "BR",
  zh: "CN",
}

const REGIONAL_INDICATOR_A = 0x1f1e6

const toFlagEmoji = (regionCode: string) =>
  String.fromCodePoint(
    ...[...regionCode.toUpperCase()].map(
      (letter) => REGIONAL_INDICATOR_A + letter.charCodeAt(0) - 65,
    ),
  )

/** Flag for a language code, e.g. `en-GB` or the bare family `en`. */
export const getLanguageFlag = (languageCode: string) => {
  const regionCode = languageCode.split("-")[1]
  if (regionCode) {
    return toFlagEmoji(regionCode)
  }

  const familyRegionCode = LANGUAGE_FAMILY_FLAG_REGIONS[languageCode]
  return familyRegionCode ? toFlagEmoji(familyRegionCode) : undefined
}

export const getLanguageDisplayName = (languageCode: string) =>
  LANGUAGE_DISPLAY_MAP[languageCode as keyof typeof LANGUAGE_DISPLAY_MAP] ||
  languageCode

export const getLanguageFamily = (languageCode: string) =>
  languageCode.split("-")[0]

const getLanguageFamilyLabel = (languageFamily: string) => {
  const directLabel =
    LANGUAGE_DISPLAY_MAP[languageFamily as keyof typeof LANGUAGE_DISPLAY_MAP]
  if (directLabel) {
    return directLabel
  }

  const regionalLanguage = SUPPORTED_LANGUAGES.find(
    (languageCode) => getLanguageFamily(languageCode) === languageFamily,
  )
  if (!regionalLanguage) {
    return languageFamily
  }

  return getLanguageDisplayName(regionalLanguage).replace(/\s*\(.+\)$/, "")
}

// Sorted by display label (not SUPPORTED_LANGUAGES order, which is not
// alphabetized) so the persona creation language dropdown lists languages
// alphabetically.
const LANGUAGE_FAMILY_OPTIONS = Array.from(
  new Set(SUPPORTED_LANGUAGES.map(getLanguageFamily)),
)
  .map((languageFamily) => ({
    value: languageFamily,
    label: getLanguageFamilyLabel(languageFamily),
  }))
  .sort((a, b) => a.label.localeCompare(b.label))

const getFamilyLanguageCodes = (languageFamily: string) =>
  SUPPORTED_LANGUAGES.filter(
    (languageCode) => getLanguageFamily(languageCode) === languageFamily,
  )

const getRegionalLanguageCodes = (languageFamily: string) =>
  getFamilyLanguageCodes(languageFamily).filter((languageCode) =>
    languageCode.includes("-"),
  )

interface LanguageFilterOption {
  value: string
  label: string
  /** A regional variant narrows its language to the voices that speak it. */
  isRegional: boolean
}

/**
 * One list for language and accent. A language's own entry is the catch-all
 * across its regions, and a regional variant is listed under it only when the
 * language has more than one region to choose between. A persisted language
 * or regional code the catalog does not list stays selectable so an existing
 * persona still shows what it saved.
 */
export const getLanguageFilterOptions = (
  selectedLanguageCode?: string,
): LanguageFilterOption[] => {
  const selectedFamily = selectedLanguageCode
    ? getLanguageFamily(selectedLanguageCode)
    : undefined
  const unlistedRegionalCode =
    selectedLanguageCode &&
    selectedFamily &&
    selectedLanguageCode !== selectedFamily &&
    !getRegionalLanguageCodes(selectedFamily).some(
      (languageCode) => languageCode === selectedLanguageCode,
    )
      ? selectedLanguageCode
      : undefined
  const familyOptions =
    selectedFamily &&
    !LANGUAGE_FAMILY_OPTIONS.some((option) => option.value === selectedFamily)
      ? [
          ...LANGUAGE_FAMILY_OPTIONS,
          {
            value: selectedFamily,
            label: getLanguageFamilyLabel(selectedFamily),
          },
        ]
      : LANGUAGE_FAMILY_OPTIONS

  return familyOptions.flatMap((familyOption) => {
    const regionalCodes: string[] = getRegionalLanguageCodes(familyOption.value)
    const listedRegionalCodes = regionalCodes.length > 1 ? regionalCodes : []
    const regionalOptions =
      unlistedRegionalCode && familyOption.value === selectedFamily
        ? [...listedRegionalCodes, unlistedRegionalCode]
        : listedRegionalCodes

    return [
      { ...familyOption, isRegional: false },
      ...regionalOptions
        .map((languageCode) => ({
          value: languageCode,
          label: getLanguageDisplayName(languageCode),
          isRegional: true,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ]
  })
}

export const getDefaultLanguageCodeForFamily = (languageFamily: string) => {
  const familyCodes: string[] = getFamilyLanguageCodes(languageFamily)
  const preferredLanguageCode = PREFERRED_LANGUAGE_CODES[languageFamily]

  if (preferredLanguageCode && familyCodes.includes(preferredLanguageCode)) {
    return preferredLanguageCode
  }

  return (
    familyCodes.find((languageCode) => languageCode === languageFamily) ??
    familyCodes[0]
  )
}

export const getVoiceAccentLabel = (voice: CovalVoice) => {
  if (voice.accentLabel) {
    return voice.accentLabel
  }

  const match = voice.displayName.match(/\(([^)]+)\)/)
  return match?.[1]
}

const getUniqueLanguageFamilies = (voice: CovalVoice) => {
  const languages = voice.languages ?? SUPPORTED_LANGUAGES
  return Array.from(new Set(languages.map(getLanguageFamily)))
}

export const isMultilingualVoice = (voice: CovalVoice) =>
  getUniqueLanguageFamilies(voice).length > 1

const getVoiceLanguageDisplayNames = (voice: CovalVoice) => {
  const languages = voice.languages ?? SUPPORTED_LANGUAGES
  return Array.from(new Set(languages.map(getLanguageDisplayName)))
}

export const getVoiceLanguageSummary = (voice: CovalVoice) => {
  const displayNames = getVoiceLanguageDisplayNames(voice)

  if (displayNames.length > 6) {
    return `${displayNames.length} language/accent options`
  }

  if (displayNames.length > 3) {
    return `${displayNames.slice(0, 2).join(", ")} +${displayNames.length - 2} more`
  }

  return displayNames.join(", ")
}

export const getVoiceFilterKey = (
  filterType: "presentation" | "pitch",
  value: string,
): VoiceFilterKey => `${filterType}:${value}` as VoiceFilterKey

const getVoiceFilterValue = (
  filterKey: VoiceFilterKey,
  filterType: "presentation" | "pitch",
) => {
  const prefix = `${filterType}:`
  if (!filterKey.startsWith(prefix)) {
    return undefined
  }

  return filterKey.slice(prefix.length)
}

const getActiveFilterValues = (
  selectedFilterKeys: Set<VoiceFilterKey>,
  filterType: "presentation" | "pitch",
) =>
  Array.from(selectedFilterKeys)
    .map((filterKey) => getVoiceFilterValue(filterKey, filterType))
    .filter((value): value is string => Boolean(value))

export const voiceMatchesFilters = (
  voice: CovalVoice,
  selectedFilterKeys: Set<VoiceFilterKey>,
) => {
  const selectedPresentationFilters = getActiveFilterValues(
    selectedFilterKeys,
    "presentation",
  )
  const selectedPitchFilters = getActiveFilterValues(
    selectedFilterKeys,
    "pitch",
  )

  if (
    selectedPresentationFilters.length > 0 &&
    !selectedPresentationFilters.includes(voice.voicePresentation)
  ) {
    return false
  }

  if (
    selectedPitchFilters.length > 0 &&
    (!voice.pitch || !selectedPitchFilters.includes(voice.pitch))
  ) {
    return false
  }

  return true
}

export const getVoiceSearchText = (voice: CovalVoice) =>
  [
    voice.displayName,
    voice.name,
    voice.voicePresentation,
    getVoiceAccentLabel(voice),
    isMultilingualVoice(voice) ? "Multilingual" : undefined,
    ...getVoiceLanguageDisplayNames(voice),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()

export const voiceSupportsLanguageFamily = (
  voice: CovalVoice,
  languageFamily?: string,
) => {
  if (!languageFamily) {
    return true
  }

  const languages = voice.languages ?? SUPPORTED_LANGUAGES
  return languages.some(
    (languageCode) => getLanguageFamily(languageCode) === languageFamily,
  )
}

export const getPreferredLanguageCodeForVoice = (
  voice: CovalVoice | undefined,
  languageFamily?: string,
  currentLanguageCode?: string,
) => {
  if (!voice || !languageFamily) {
    return currentLanguageCode
  }

  // A bare family code is the catch-all, not a region the user chose, so a
  // voice that speaks the family's default region resolves to it.
  if (
    currentLanguageCode &&
    currentLanguageCode !== languageFamily &&
    voiceSupportsLanguageCode(voice, currentLanguageCode)
  ) {
    return currentLanguageCode
  }

  const preferredLanguageCode = PREFERRED_LANGUAGE_CODES[languageFamily]
  if (
    preferredLanguageCode &&
    voiceSupportsLanguageCode(voice, preferredLanguageCode)
  ) {
    return preferredLanguageCode
  }

  if (voiceSupportsLanguageCode(voice, languageFamily)) {
    return languageFamily
  }

  return voice.languages?.find(
    (languageCode) => getLanguageFamily(languageCode) === languageFamily,
  )
}
